import { api, Banner, Empty, stackOnMobile, useAsync } from "@getaways/extranet-ui";
import { IconDownload } from "@tabler/icons-react";
import { useState } from "react";
import type { Filters } from "./filters";
import { BRANDS, eur, int, STATUS, VERTICALS } from "./format";
import { ScheduleButton } from "./schedule";

/** QuerySpec and QueryResult of @getaways/reports, as the API takes and returns them. */
export interface Spec {
  table: "orders" | "items" | "movements" | "funnel";
  measures: string[];
  dimensions?: string[];
  filter?: Record<string, unknown>;
  kinds?: string[];
  sort?: string;
  dir?: "asc" | "desc";
  limit?: number;
}

export interface Column {
  key: string;
  label: string;
  type: "dimension" | "count" | "eur" | "pct";
}

export interface Result {
  columns: Column[];
  rows: Record<string, string | number | null>[];
  totals: Record<string, number | null>;
  truncated: boolean;
}

/** The API's filter from the page's filter bar; `extra` adds or overrides fields. */
export function filterOf(f: Filters, extra: Record<string, unknown> = {}): Record<string, unknown> {
  return {
    from: f.range.from,
    to: f.range.to,
    basis: f.basis,
    status: f.status,
    ...(f.brand ? { brands: [f.brand] } : {}),
    ...(f.market ? { markets: [f.market] } : {}),
    ...(f.supplier ? { suppliers: [f.supplier] } : {}),
    ...extra,
  };
}

export function useQuery(spec: Spec | undefined) {
  const key = JSON.stringify(spec ?? null);
  return useAsync(() => (spec ? api<Result>("POST", "/v1/query", spec) : Promise.resolve(undefined)), [key]);
}

/** A cell as people read it: euros, percentages, names for codes. */
export function formatCell(col: Column | undefined, v: string | number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  if (!col || col.type === "dimension") {
    const s = String(v);
    if (col?.key === "brand") return BRANDS[s] ?? s;
    if (col?.key === "vertical") return VERTICALS[s] ?? s;
    if (col?.key === "status") return STATUS[s] ?? s.replace(/_/g, " ");
    return s;
  }
  if (col.type === "eur") return eur(Number(v));
  if (col.type === "pct") return `${Number(v).toFixed(1)}%`;
  return int(Number(v));
}

/** Downloads the spec's rows as CSV or Excel, with the person's scope (POST /v1/export). */
export function ExportButtons({ spec, title }: { spec: Spec; title: string }) {
  const [error, setError] = useState<string>();
  const download = async (format: "csv" | "xlsx") => {
    setError(undefined);
    const res = await fetch("/v1/export", { method: "POST", headers: { "content-type": "application/json" }, credentials: "same-origin", body: JSON.stringify({ spec, format, title }) });
    if (!res.ok) return setError(((await res.json().catch(() => ({}))) as { message?: string }).message ?? `Export failed (${res.status})`);
    const name = /filename="([^"]+)"/.exec(res.headers.get("content-disposition") ?? "")?.[1] ?? `report.${format}`;
    const url = URL.createObjectURL(await res.blob());
    const a = Object.assign(document.createElement("a"), { href: url, download: name });
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return (
    <span className="row" style={{ gap: 6 }}>
      <button className="ghost" onClick={() => void download("xlsx")} title="Download as Excel"><IconDownload size={14} /> Excel</button>
      <button className="ghost" onClick={() => void download("csv")} title="Download as CSV">CSV</button>
      {error && <span className="small" style={{ color: "var(--bad)" }}>{error}</span>}
    </span>
  );
}

/** A query's result as a table, with a totals line and the export buttons. */
export function ResultTable({ title, spec, label, empty = "Nothing in this period." }: { title: string; spec: Spec; label?: (col: string, v: string | number | null) => string | undefined; empty?: string }) {
  const { data, error } = useQuery(spec);
  return (
    <section className="card" style={{ padding: 0 }}>
      <div className="row" style={{ padding: "12px 14px", borderBottom: "1px solid var(--line)" }}>
        <h2 style={{ margin: 0, fontSize: 15 }}>{title}</h2>
        <span className="spacer" />
        <ExportButtons spec={spec} title={title} />
        <ScheduleButton spec={spec} title={title} />
      </div>
      {error && <Banner notice={{ tone: "bad", text: error }} />}
      {data && data.rows.length === 0 ? <Empty>{empty}</Empty> : (
        <table ref={stackOnMobile} className="table stack-mobile">
          <thead><tr>{(data?.columns ?? []).map((c) => <th key={c.key} className={c.type === "dimension" ? "" : "num"}>{c.label}</th>)}</tr></thead>
          <tbody>
            {(data?.rows ?? []).map((r, i) => (
              <tr key={i}>{data!.columns.map((c) => <td key={c.key} className={c.type === "dimension" ? "" : "num"}>{label?.(c.key, r[c.key] ?? null) ?? formatCell(c, r[c.key])}</td>)}</tr>
            ))}
            {data && data.columns.some((c) => c.type === "dimension") && data.rows.length > 1 && (
              <tr className="total">{data.columns.map((c, i) => <td key={c.key} className={c.type === "dimension" ? "" : "num"}><strong>{c.type === "dimension" ? (i === 0 ? "Total" : "") : formatCell(c, data.totals[c.key])}</strong></td>)}</tr>
            )}
          </tbody>
        </table>
      )}
      {data?.truncated && <p className="small muted" style={{ padding: "8px 14px" }}>Only the first {data.rows.length} lines; the export has up to 5,000.</p>}
    </section>
  );
}

/** Compact euros for chart axes: €12k, €1.2M. */
export const compactEur = (minor: number) => {
  const v = minor / 100;
  return Math.abs(v) >= 1e6 ? `€${(v / 1e6).toFixed(1)}M` : Math.abs(v) >= 1e3 ? `€${Math.round(v / 1e3)}k` : `€${Math.round(v)}`;
};
